import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { PlusCircle } from 'lucide-react';
import TripScheduleView from './TripScheduleView';
import TripScheduleBrowseLayout from './TripScheduleBrowseLayout';
import SharedTripsEmptyState from './SharedTripsEmptyState';
import {
  filterUpcomingTrips,
  filterTripsInDayRange,
  getQuickDateFilter,
} from '../utils/tripFilters';

const DAY_RANGE = 7;

const HomeTripScheduleSection = ({
  trips,
  loading,
  selectedTrip,
  onSelectTrip,
  onCloseTrip,
  isUserJoined,
  isTripCreator,
  isAuthenticated,
  userId = null,
  onJoin,
  /** When true: calendar-only teaser — no quick date pills or browse/post footer. */
  homeTeaser = false,
}) => {
  const [dateFilter, setDateFilter] = useState('');

  const upcomingTrips = useMemo(() => filterUpcomingTrips(trips), [trips]);

  const scheduleTrips = useMemo(() => {
    let list = filterTripsInDayRange(upcomingTrips, DAY_RANGE);
    if (dateFilter) {
      list = list.filter((t) => t.travel_date === dateFilter);
    }
    return list;
  }, [upcomingTrips, dateFilter]);

  const handleScheduleSelect = (trip) => {
    if (selectedTrip?.id === trip.id) {
      onCloseTrip?.();
      return;
    }
    onSelectTrip(trip);
    requestAnimationFrame(() => {
      document.getElementById('trip-schedule-detail')?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    });
  };

  const panelProps = {
    isJoined: selectedTrip ? isUserJoined(selectedTrip) : false,
    isCreator: selectedTrip ? isTripCreator(selectedTrip) : false,
    isAuthenticated,
    userId,
    onJoin,
    onClose: onCloseTrip,
  };

  return (
    <section id="trips-schedule" className="scroll-mt-24">
      <div className="flex flex-col md:flex-row items-start md:items-end justify-between mb-8 gap-4">
        <div>
          <h2 className="text-3xl font-serif font-bold text-[#2D1E17]">
            {homeTeaser ? 'Upcoming shared transfers' : 'Upcoming Trips'}
          </h2>
          <p className="text-stone-500 mt-1">
            {homeTeaser
              ? 'Travelling on a budget outside our scheduled bus service? Start a shared transfer and other passengers can join.'
              : 'Browse by day — tap a trip to see details and join.'}
          </p>
        </div>
        {!homeTeaser && (
          <div className="flex flex-wrap gap-2">
            {[
              { label: 'Today', key: 'today' },
              { label: 'Tomorrow', key: 'tomorrow' },
              { label: 'This Weekend', key: 'thisWeekend' },
            ].map(({ label, key }) => {
              const val = getQuickDateFilter(key);
              const isActive = dateFilter === val;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setDateFilter(isActive ? '' : val)}
                  className={`px-4 py-2 rounded-full border text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-[#A44432] text-white border-[#A44432]'
                      : 'border-stone-200 hover:border-[#A44432] hover:text-[#A44432]'
                  }`}
                >
                  {label}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {loading ? (
        <div className="text-center py-16">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#A44432] mx-auto" />
          <p className="mt-4 text-stone-500">Loading trips...</p>
        </div>
      ) : scheduleTrips.length === 0 ? (
        <SharedTripsEmptyState compact={homeTeaser} />
      ) : (
        <>
          <TripScheduleBrowseLayout
            selectedTrip={selectedTrip}
            panelProps={panelProps}
            schedule={
              <TripScheduleView
                trips={scheduleTrips}
                dayCount={DAY_RANGE}
                selectedTripId={selectedTrip?.id ?? null}
                onSelectTrip={handleScheduleSelect}
                isUserJoined={isUserJoined}
              />
            }
          />

          {!homeTeaser && (
            <div className="mt-10 flex flex-col sm:flex-row items-center justify-between gap-4 pt-6 border-t border-[#E8DED1]/80">
              <p className="text-stone-500 text-sm">Can&apos;t find your trip?</p>
              <div className="flex flex-wrap items-center gap-3">
                <Link to="/trips" className="btn-secondary inline-flex items-center gap-2">
                  <PlusCircle size={18} />
                  Browse all trips
                </Link>
                <Link to="/create" className="text-sm text-[#A44432] font-medium hover:underline">
                  Start a shared trip
                </Link>
              </div>
            </div>
          )}
        </>
      )}
    </section>
  );
};

export default HomeTripScheduleSection;
